import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr';
import { theme } from '@theme';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { NotificationHeaderType, NotificationType } from '../../enums';
import { useCheckDevice } from '../../hooks/UseCheckDevice';
import { useConfig } from '../../hooks/UseConfig';
import { useHeapTrack } from '../../hooks/UseHeapTrack';
import { useSearchDeals } from '../../hooks/header/UseSearchDeals';
import { GetToken, formatDateWithReference } from '../../services/utils';
import {
  getNotificationIcon,
  sortNotifications,
  updateHash,
  updateNotification,
} from '../../services/utils/NotificationUtils';
import { AppState } from '../../store/AppState';
import { setLoadNotificationsSuccessAction } from '../../store/notifications/NotificationActions';
import { NotificationResource } from '../../types';
import { DealerState } from '../../types/DealerState';
import { IF, StringToJSX } from '../shared/generics';
import { DealTypeSticker } from '../style/DealTypeSticker';
import { BPS, CarModel, Copy, ListItem } from './SharedNotificationStyles';

export type RealTimeNotificationProps = {
  storeCode: string;
  activeFilter: NotificationHeaderType;
  isNotificationWindowOpen: boolean;
  onClick: (item: NotificationResource) => void;
  notifications: NotificationResource[];
};

export const RealTimeNotification: FC<RealTimeNotificationProps> = (props) => {
  const { signalrUrl } = useConfig();
  const dispatch = useDispatch();

  const [item, setItem] = useState<NotificationResource>(null);
  const [clickedItem, setClickedItem] = useState<NotificationResource>(null);
  const { transUnionStatus } = useSelector<AppState, DealerState>(
    (dealerState) => dealerState.dealer,
  );
  const { isDealerOptIn } = useSearchDeals();
  const { isMobileDevice } = useCheckDevice();

  const ammNotificationPopupStyle = {
    backgroundImage: 'none',
    backgroundColor: theme.palette.white,
    color: theme.palette.black,
    padding: '0',
    width: isMobileDevice ? '97vw' : '480px',
    OverflowY: 'hidden',
    opacity: 1,
    margin: isMobileDevice ? '6px' : '',
    maxWidth: '480px',
  };

  const setClickedItemFromToast = (
    notification: NotificationResource,
    event: React.MouseEvent<Element>,
  ) => {
    if ((event.target as HTMLElement).closest('.toast-close-button')) {
      return;
    }

    const privateIncentiveType =
      notification.isConquest && notification.isPrivateIncentiveApplied
        ? 'Service'
        : notification.isPrivateIncentiveApplied
          ? 'Loyalty'
          : '';

    if (privateIncentiveType !== '') {
      useHeapTrack(
        'Automated PIL - Service Arrival Notification with ' +
          privateIncentiveType +
          ' Private Incentive',
        {
          StoreCode: props.storeCode,
          DealerId: notification.dealerId,
        },
      );
    }

    setClickedItem(notification);
  };

  const [connection, setConnection] = useState<HubConnection>();

  useEffect(() => {
    const authorizationHeaderValue = GetToken('user');
    const token = authorizationHeaderValue ?? '';
    const retryTimes = [0, 3000, 10000, 30000, 90000, 300000, 600000, 1500000];
    const newConnection = new HubConnectionBuilder()
      .withUrl(`${signalrUrl}/signalr?storeCode=${props.storeCode}`, {
        accessTokenFactory: () => token,
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (context) => {
          const index =
            context.previousRetryCount < retryTimes.length
              ? context.previousRetryCount
              : retryTimes.length - 1;
          return retryTimes[index];
        },
      })
      .build();

    setConnection(newConnection);
  }, [props.storeCode]);

  const startHubConnection = () => {
    if (connection && connection.state === HubConnectionState.Disconnected) {
      connection
        .start()
        .then(() => {
          console.log('Connection started');
        })
        .catch((err) => {
          console.log('Error while starting connection: ' + err);
        });

      connection.on('sendNotification', (notification: NotificationResource) => {
        if (
          !isMobileDevice ||
          notification.notificationType !== NotificationType.ConquestCampaignClick
        ) {
          updateNotification(notification, transUnionStatus);
          updateHash(notification);
          setItem(notification);
        }
      });

      connection.on('sendCrmStatusNotification', (notification: NotificationResource) => {
        const crmStatusNotificationEvent = new CustomEvent('crm-status-notification', {
          detail: notification,
        });
        window.dispatchEvent(crmStatusNotificationEvent);
      });
    }
  };

  useEffect(() => {
    if (item) {
      let items = [...props.notifications];
      const previousItemIndex = items.findIndex((x) => x.closedDealId === item.closedDealId);
      if (previousItemIndex !== -1) {
        items[previousItemIndex] = item;
      } else {
        items.push(item);
        items = sortNotifications(items, props.activeFilter);
      }

      dispatch(setLoadNotificationsSuccessAction(items));
      setTimeout(() => {
        makeNotificationPopup(item);
      }, 0);
    }
  }, [item]);

  useEffect(() => {
    if (clickedItem) {
      props.onClick(clickedItem);
    }
  }, [clickedItem]);

  const getNotificationToast = (notification: NotificationResource): JSX.Element => {
    const iconClass = `am-icon ${getNotificationIcon(notification)}`;
    let mdlIconClass = 'hidden';
    let conquestIconClass = 'hidden';
    let incentiveIconClass = 'hidden';

    if (notification.isConquest) {
      conquestIconClass = 'am-icon am-icon-conquest float-right';
      if (
        notification.notificationType === 'MdlServiceArrival' &&
        notification.notificationValue !== 'CDK'
      ) {
        mdlIconClass += ' mdl-with-conquest-icon';
      }
    }

    if (
      notification.isPrivateIncentiveApplied &&
      (!notification.isConquest || // loyalty private incentive
        (notification.isConquest && isDealerOptIn(notification.dealerId))) // service private incentive
    ) {
      incentiveIconClass = 'am-icon am-icon-private-incentive float-right';
    }

    return (
      <List isMobileDevice={isMobileDevice} data-testid='list-item'>
        <i className={iconClass}></i>
        <Stack data-testid='stack' isMobileDevice={isMobileDevice}>
          <Copy className={notification.isTextWithIcon ? 'text-with-icon' : ''} data-testid='copy'>
            <span>
              {' '}
              <StringToJSX domString={notification.notificationText} />
            </span>
          </Copy>
          <CarModelRealTime
            isMarketConquest={notification.notificationValue === 'MarketConquest'}
            data-testid='car-model'
          >
            {notification.vehicleInfo}
            <IF condition={notification.date && !!notification.vehicleInfo}>
              <span>.&nbsp;</span>
            </IF>
            <IF
              condition={notification.date && notification.notificationType != 'PrivateIncentive'}
            >
              <b> {formatDateWithReference(notification.date)}</b>
            </IF>
          </CarModelRealTime>
        </Stack>
        <i className={conquestIconClass}></i>
        <i className={incentiveIconClass}></i>
        <i className={mdlIconClass}></i>
        <BPSAbsolute data-testid='bps'>
          <em>
            BPS <span>&reg;</span>
          </em>
          <b>{notification.bps}</b>
        </BPSAbsolute>
        <Actions data-testid='actions'>
          <DealTypeStickerRealTime
            data-testid='deal-type-sticker'
            additionalClassName={item.dealType}
            title={item.dealType + ' Deal'}
          >
            {item.dealType.charAt(0).toUpperCase()}
          </DealTypeStickerRealTime>
          <div>
            <div></div>
          </div>
        </Actions>
      </List>
    );
  };

  const makeNotificationPopup = (notification: NotificationResource) => {
    // if notifications menu is open, do not show popup.
    if (props.isNotificationWindowOpen) {
      return;
    }
    // AMCustomToast(getNotificationToast(notification), 5000, true)
    const iconClassForToast =
      notification.notificationValue !== 'MarketConquest'
        ? 'amm-notification-popup'
        : 'amm-notification-popup market-conquest';
    toast(getNotificationToast(notification), {
      autoClose: 5000,
      style: ammNotificationPopupStyle,
      className: iconClassForToast,
      onClick: (event) => setClickedItemFromToast(notification, event),
      closeButton: getCloseIcon,
    });
  };

  const getCloseIcon = ({ closeToast }) => {
    const handleCloseButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      closeToast();
    };
    return (
      <ToastCloseButton
        data-testid='toast-close-button'
        onClick={handleCloseButtonClick}
        role='button'
      >
        <i className='am-icon am-icon-x close'></i>
      </ToastCloseButton>
    );
  };

  useEffect(() => {
    startHubConnection();
  }, [connection]);

  return <></>;
};

export const Actions = styled.div`
  position: absolute;
  right: -25px;
  top: 38px;
  text-align: center;
  .tooltip {
    white-space: nowrap;
  }
`;

const Stack = styled.div<{ isMobileDevice: boolean }>`
  display: flex;
  flex-direction: column;
  margin: 0 5px;
  width: ${(props) => (props.isMobileDevice ? '53%' : '280px')};
  max-width: 280px;
`;

const BPSAbsolute = styled(BPS)`
  position: absolute !important;
  right: 0;
  background: ${theme.palette.lightPurpleTwo};
  border-radius: 4px;
  text-align: center;
  top: 10px;
  padding: 5px;
  min-width: 44px;

  em {
    font-size: ${theme.typography.fontSize14}px;
    color: ${theme.palette.darkPurpleTwo};
    line-height: 16px;
    display: block;
    font-style: normal;
  }
  b {
    font-size: ${theme.typography.fontSize20}px;
    color: ${theme.palette.darkPurpleThree};
    line-height: 1;
    span {
      color: ${theme.palette.dusty};
      vertical-align: top;
      font-size: 80%;
    }
  }
`;

const ToastCloseButton = styled.div.attrs({
  className: 'toast-close-button',
})`
  z-index: 1;
  position: relative;
  top: 5px;
  right: 0;
  opacity: 1;
  i {
    padding: 3px 11px 0 0;
    font-size: ${theme.typography.fontSize16}px;
  }
  &:hover {
    cursor: pointer;
    opacity: 0.4;
  }
`;

const CarModelRealTime = styled(CarModel)<{ isMarketConquest: boolean }>`
  ${(props) => (props.isMarketConquest ? 'padding: 0 10px' : '')}
`;

const DealTypeStickerRealTime = styled(DealTypeSticker)`
  position: unset;
  opacity: 0.85;
`;

const List = styled(ListItem)<{ isMobileDevice: boolean }>`
  width: ${(props) => (props.isMobileDevice ? '87vw' : '440px !important')};
  max-width: 440px;
  margin: 0;
  display: flex;
  flex-direction: row;
  padding: ${(props) => (props.isMobileDevice ? '0px' : '0px 0px 0px 10px')};
  color: ${theme.palette.basicGray};
  font-size: ${theme.typography.fontSize14}px;
  font-weight: ${theme.typography.fontWeights.normal};
  list-style: none;
  min-height: 69px;
  border-bottom: 0px;
  user-select: none;
  &.empty {
    padding: 10px;
    p {
      line-height: 44px;
    }
  }
  &.service {
    padding-right: 130px;
  }
  p {
    margin: 0 0 5px;
    line-height: 1.2;
  }
`;
